/**
 * Fluent UI styles for the FieldMappingEditor family of components.
 */
import { makeStyles, tokens } from "@fluentui/react-components";

export const useFieldMappingStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  /* ── Field group ────────────────────────────────────────────────── */
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldGroupHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "2px",
  },
  fieldLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  fieldHelper: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground4,
    marginTop: "2px",
  },

  /* ── Shared bordered box for X axis (category + sort/limit) ──────── */
  xAxisBox: {
    display: "flex",
    flexDirection: "column" as const,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "6px",
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  /* ── Top row inside xAxisBox: category dropdown ─────────────────── */
  xAxisCategoryRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 8px",
  },
  /* ── Bottom row inside xAxisBox: sort + limit ───────────────────── */
  sortLimitRow: {
    display: "flex",
    flexWrap: "nowrap" as const,
    alignItems: "center",
    gap: "6px",
    padding: "4px 8px",
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    minWidth: 0,
    minHeight: "32px",
    overflow: "hidden",
  },
  /* ── Standalone pill row (used when no sort/limit) ──────────────── */
  pillRow: {
    display: "flex",
    flexWrap: "nowrap" as const,
    alignItems: "center",
    gap: "6px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "6px",
    padding: "4px 8px",
    backgroundColor: tokens.colorNeutralBackground1,
    minWidth: 0,
  },
  sortDropdown: {
    minWidth: 0,
    flex: "1 1 auto",
    overflow: "hidden",
    "& button": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  limitDropdown: {
    minWidth: 0,
    flexShrink: 0,
    flexBasis: "100px",
    "& button": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  limitRow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "6px",
    padding: "4px 8px",
    backgroundColor: tokens.colorNeutralBackground1,
    width: "fit-content",
  },
  customLimitInput: {
    width: "70px",
  },
  othersCheckbox: {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
    flexShrink: 0,
    whiteSpace: "nowrap",
    "& label": {
      padding: 0,
      margin: 0,
    },
  },

  dropdown: {
    flex: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    justifyContent: "flex-start",
    textAlign: "left" as const,
    "& .fui-Button__content": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flex: 1,
      textAlign: "left" as const,
    },
    "& .fui-Button__icon": {
      marginLeft: "auto",
      fontSize: "12px",
      flexShrink: 0,
      color: tokens.colorNeutralForeground4,
    },
  },

  /* ── Measure list ───────────────────────────────────────────────── */
  measureList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  measurePill: {
    display: "grid",
    gridTemplateColumns: "1fr 100px",
    alignItems: "center",
    gap: "8px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "6px",
    padding: "4px 8px",
    backgroundColor: tokens.colorNeutralBackground1,
    touchAction: "none",
  },
  measurePillWithDrag: {
    gridTemplateColumns: "16px 1fr 100px",
  },
  measurePillWithRemove: {
    gridTemplateColumns: "1fr 100px 28px",
  },
  measurePillWithDragAndRemove: {
    gridTemplateColumns: "16px 1fr 100px 28px",
  },
  /* No-aggregation variants (scatter raw mode) — column picker takes full width */
  measurePillNoAggDragRemove: {
    gridTemplateColumns: "16px 1fr 28px",
  },
  measurePillNoAggDrag: {
    gridTemplateColumns: "16px 1fr",
  },
  measurePillNoAggRemove: {
    gridTemplateColumns: "1fr 28px",
  },
  measureDrag: {
    cursor: "grab",
    opacity: 0.5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    color: tokens.colorNeutralForeground4,
  },
  measureField: {
    minWidth: 0,
    overflow: "hidden",
    whiteSpace: "nowrap",
    justifyContent: "flex-start",
    textAlign: "left" as const,
    "& .fui-Button__content": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      flex: 1,
      textAlign: "left" as const,
    },
    "& .fui-Button__icon": {
      marginLeft: "auto",
      fontSize: "12px",
      flexShrink: 0,
      color: tokens.colorNeutralForeground4,
    },
  },
  aggDropdown: {
    minWidth: 0,
    width: "110px",
    flexShrink: 0,
  },

  /* ── Column listbox popup (shared across all column-picker dropdowns) ── */
  columnListbox: {
    minWidth: "320px",
    maxHeight: "300px",
    overflowY: "auto" as const,
  },

  /* ── Dropdown option ────────────────────────────────────────────── */
  optionContent: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
  },
  optionName: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionBadge: {
    fontSize: "9px",
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground4,
    backgroundColor: tokens.colorNeutralBackground4,
    padding: "1px 5px",
    borderRadius: tokens.borderRadiusSmall,
    letterSpacing: "0.3px",
    flexShrink: 0,
  },
});